import { RadarDtg } from 'src/app/radar/radar.service';

import { environment } from 'src/environments/environment';

export type RadarRegion = 'de' | 'benelux' | 'es' | 'fr' | 'ch' | 'eu';

interface RadarLayerConfig {
  obs: string;
  fcst: string;
  tracking?: string;
}

const RADAR_LAYERS_BY_REGION: Record<RadarRegion, RadarLayerConfig> = {
  de: {
    obs: 'PRECIPITATIONTYPE',
    fcst: 'PRECIPITATIONTYPEFCST',
    tracking: 'PRECIPITATIONTYPE%2CTRACKING',
  },
  benelux: {
    obs: 'PRECIPITATIONTYPEBENELUX',
    fcst: 'PRECIPITATIONTYPEFCSTBENELUX',
    tracking: 'PRECIPITATIONTYPEBENELUX%2CTRACKINGBENELUX',
  },
  es: { obs: 'PRECIPITATIONTYPEES', fcst: 'PRECIPITATIONTYPEFCSTES' },
  fr: {
    obs: 'PRECIPITATIONTYPEFR',
    fcst: 'PRECIPITATIONTYPEFCSTFR',
    tracking: 'PRECIPITATIONTYPEFR%2CTRACKINGFR',
  },
  ch: {
    obs: 'PRECIPITATIONTYPECH',
    fcst: 'PRECIPITATIONTYPEFCSTCH',
    tracking: 'PRECIPITATIONTYPECH%2CTRACKINGCH',
  },
  eu: { obs: 'PRECIPITATIONEU', fcst: 'PRECIPITATIONTYPEFCSTEU' },
};

const isRadarRegion = (region: string): region is RadarRegion => {
  return Object.prototype.hasOwnProperty.call(RADAR_LAYERS_BY_REGION, region);
};

export const hasTrackingLayer = (region: string): boolean =>
  isRadarRegion(region) && !!RADAR_LAYERS_BY_REGION[region].tracking;

const pad = (num: number, size: number): string => {
  let value = String(num);
  while (value.length < size) {
    value = '0' + value;
  }
  return value;
};

/**
 * 1. Generiert die primäre WMS-Kachel-URL basierend auf den Steuerungs-Parametern.
 * (Behält den Platzhalter {bbox-epsg-3857} für Mapbox GL / OpenLayers bei)
 */
export const getRadarTileUrl = (
  dtg: RadarDtg,
  region: string,
  showCellTracking: boolean
): string | null => {
  if (!dtg || !isRadarRegion(region)) {
    return null;
  }

  const regionLayers = RADAR_LAYERS_BY_REGION[region];

  let url =
    environment.url.mapServer +
    '/fcgi-bin/mapserv?map=/srv/mapserver/maps/radar/radar_map.map&service=WMS&request=GetMap' +
    '&styles=&format=image%2Fpng&transparent=true&version=1.3.0&width=1024&height=1024&crs=EPSG%3A3857&bbox={bbox-epsg-3857}';

  if (dtg.isFcst) {
    url += '&layers=' + regionLayers.fcst + '&ISSUE=' + pad(dtg.issue, 2);
  } else if (showCellTracking && !dtg.isArchive && regionLayers.tracking) {
    // Primärer Wunsch: Niederschlagsart gekoppelt mit Cell-Tracking Sturmzellen-Layer
    url += '&layers=' + regionLayers.tracking;
  } else {
    // Standard-Beobachtung ohne Cell-Tracking
    url += '&layers=' + regionLayers.obs;
  }

  url += '&TIME=' + dtg.mapServer;
  return url;
};

/**
 * 2. ASYNCHRONER RECOVERY-CHECKER (Der automatisierte Fallback-Hebel)
 * Prüft vorab, ob der Mapserver valide PNG-Daten liefert oder eine XML-ServiceException wirft.
 * Wenn die Cell-Tracking-Kombination fehlschlägt, wird sofort die bereinigte Fallback-URL geliefert.
 *
 * @param testBbox Eine temporäre, reelle EPSG:3857 Bounding-Box für den HTTP-Validierungstest.
 */
export const resolveValidRadarTileUrl = async (
  dtg: RadarDtg,
  region: string,
  showCellTracking: boolean,
  testBbox: string = '0,5009377,2504688,7514065' // Standardisierte Test-BBox für Deutschland
): Promise<string | null> => {
  const primaryUrl = getRadarTileUrl(dtg, region, showCellTracking);
  if (!primaryUrl) return null;

  // Ein Fallback ist nur relevant, wenn Cell-Tracking aktiv angefordert wurde
  if (
    !showCellTracking ||
    dtg.isFcst ||
    dtg.isArchive ||
    !hasTrackingLayer(region)
  ) {
    return primaryUrl;
  }

  // Erzeugt eine reelle Test-URL, da der Mapserver mit dem rohen {bbox}-String im Fetch abstürzt
  const validationUrl = primaryUrl.replace('{bbox-epsg-3857}', testBbox);

  try {
    // Wir senden einen schnellen HEAD/GET Request, um den Content-Type des Mapservers auszuwerten
    const response = await fetch(validationUrl, { method: 'GET' });

    if (response.ok) {
      const contentType = response.headers.get('content-type') || '';

      // Wenn der Mapserver XML (ServiceExceptionReport) liefert, fehlen S3-Klassen
      if (contentType.includes('xml') || contentType.includes('text')) {
        console.warn(
          `[Radar WMS] Cell-Tracking lieferte XML-Exception für DTG ${dtg.mapServer}. Starte Fallback-Routing...`
        );
        // Erzeugt die sichere Rückfall-URL ohne den TRACKING-Layer
        return getRadarTileUrl(dtg, region, false);
      }
    }

    return primaryUrl; // Response ist PNG -> Alles in Ordnung!
  } catch (error) {
    console.error(
      '[Radar] Netzwerk-Timeout beim Mapserver Validierungs-Fetch:',
      error
    );
    return getRadarTileUrl(dtg, region, false); // Bei Netzwerk-Abriss präventiv Fallback wählen
  }
};

/**
 * 3. Prüft synchron, ob die grundlegenden Konfigurations-Parameter gültig sind
 */
export const hasValidRadarTileUrl = (
  dtg: RadarDtg,
  region: string,
  showCellTracking: boolean
): boolean => {
  return getRadarTileUrl(dtg, region, showCellTracking) !== null;
};
